<template>
    <div>
        <header class="ezcache-screen-header">
            <svg viewBox="0 0 24 24">
                <path d="M22.7,19L13.6,9.9C14.5,7.6 14,4.9 12.1,3C10.1,1 7.1,0.6 4.7,1.7L9,6L6,9L1.6,4.7C0.4,7.1 0.9,10.1 2.9,12.1C4.8,14 7.5,14.5 9.8,13.6L18.9,22.7C19.3,23.1 19.9,23.1 20.3,22.7L22.6,20.4C23.1,20 23.1,19.3 22.7,19Z" />
            </svg>
            {{ $wp.trans('advanced_settings') }}
        </header>

        <form method="post" @submit.prevent="saveSettings">
            <TogglePanel :showSpinner="isLoading">
                <template #title>{{ $wp.trans('performance_settings') }}</template>

                <div class="row">
                    <div class="col-12 my-2">
                        <input type="checkbox" class="checkbox" :id="$id('combine_css_footer')" v-model="settings.combine_css_footer" :disabled="!settings.combine_css || isLoading || isSaving">
                        <label :for="$id('combine_css_footer')" v-text="$wp.trans('combine_css_footer')"></label>

                        <p class="description" v-text="$wp.trans('combine_css_footer_description')"></p>
                        <p v-if="settings.combine_css === false" class="notice notice-info notice-inline" v-text="$wp.trans('combine_css_footer_requires_combine_css_notice')"></p>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-12 my-2">
                        <label :for="$id('critical_css')">{{ $wp.trans('critical_css') }}</label>

                        <p class="description" v-text="$wp.trans('critical_css_description')"></p>
                        <textarea :id="$id('critical_css')" class="large-text ltr" v-model="settings.critical_css" rows="7" cols="15"></textarea>
                        <p class="description">
                            <a class="button button-small" href="https://web.dev/extract-critical-css/" rel="noopener noreferrer" target="_blank" v-text="$wp.trans('critical_css_more_information')"></a>
                            <a class="button button-small" href="https://www.corewebvitals.io/tools/critical-css-generator" rel="noopener noreferrer" target="_blank" v-text="$wp.trans('critical_css_online_tool')"></a>
                        </p>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-12 my-2">
                        <label :for="$id('excluded_minify_files')">{{ $wp.trans('excluded_minify_files') }}</label>

                        <p class="description" v-text="$wp.trans('excluded_minify_files_description')"></p>
                        <textarea :id="$id('excluded_minify_files')" class="large-text ltr" v-model="settings.excluded_minify_files" rows="7" cols="15" :placeholder="$wp.trans('excluded_minify_files_placeholder')"></textarea>
                    </div>
                </div>
            </TogglePanel>

            <TogglePanel :showSpinner="isLoading">
                <template #title>{{ $wp.trans('cache_bypass_settings') }}</template>

                <div class="row">
                    <div class="col-12 my-2">
                        <label v-text="$wp.trans('bypass_cache_title')"></label>
                        <span class="mx-2">
                            <button type="button" class="wpb-button wpb-button-sm wpb-button-outlined wpb-button-primary" v-text="$wp.trans('select_all')" @click.prevent="updateSettings(true, 'single', 'pages', 'frontpage', 'home', 'archives', 'tag', 'category', 'feed', 'search', 'author')"></button>
                            <button type="button" class="wpb-button wpb-button-sm wpb-button-outlined wpb-button-primary" v-text="$wp.trans('select_none')" @click.prevent="updateSettings(false, 'single', 'pages', 'frontpage', 'home', 'archives', 'tag', 'category', 'feed', 'search', 'author')"></button>
                        </span>
                    </div>

                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_single')" v-model="settings.bypass_cache.single" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_single')" v-text="$wp.trans('bypass_cache_single')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_pages')" v-model="settings.bypass_cache.pages" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_pages')" v-text="$wp.trans('bypass_cache_pages')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_frontpage')" v-model="settings.bypass_cache.frontpage" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_frontpage')" v-text="$wp.trans('bypass_cache_frontpage')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_home')" v-model="settings.bypass_cache.home" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_home')" v-text="$wp.trans('bypass_cache_home')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_archives')" v-model="settings.bypass_cache.archives" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_archives')" v-text="$wp.trans('bypass_cache_archives')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_tag')" v-model="settings.bypass_cache.tag" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_tag')" v-text="$wp.trans('bypass_cache_tag')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_category')" v-model="settings.bypass_cache.category" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_category')" v-text="$wp.trans('bypass_cache_category')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_feed')" v-model="settings.bypass_cache.feed" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_feed')" v-text="$wp.trans('bypass_cache_feed')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_search')" v-model="settings.bypass_cache.search" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_search')" v-text="$wp.trans('bypass_cache_search')"></label>
                    </div>
                    <div class="col-12 mt-1">
                        <input type="checkbox" class="checkbox" :id="$id('bypass_cache_author')" v-model="settings.bypass_cache.author" :disabled="isLoading || isSaving">
                        <label class="font-weight-normal" :for="$id('bypass_cache_author')" v-text="$wp.trans('bypass_cache_author')"></label>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-12 my-2">
                        <label :for="$id('rejected_uri')">{{ $wp.trans('rejected_uri') }}</label>

                        <p class="description" v-text="$wp.trans('rejected_uri_description')"></p>
                        <textarea :id="$id('rejected_uri')" class="large-text ltr" v-model="settings.rejected_uri" rows="7" cols="15" :placeholder="$wp.trans('rejected_uri_placeholder')"></textarea>
                        <p class="description" v-text="$wp.trans('rejected_uri_wildcard')"></p>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-12 my-2">
                        <label :for="$id('rejected_user_agent')">{{ $wp.trans('rejected_user_agent') }}</label>

                        <p class="description" v-text="$wp.trans('rejected_user_agent_description')"></p>
                        <textarea :id="$id('rejected_user_agent')" class="large-text ltr" v-model="settings.rejected_user_agent" rows="7" cols="15" :placeholder="$wp.trans('rejected_user_agent_placeholder')"></textarea>
                    </div>
                </div>

                <div class="row mt-3">
                    <div class="col-12 my-2">
                        <label :for="$id('rejected_cookies')">{{ $wp.trans('rejected_cookies') }}</label>

                        <p class="description" v-text="$wp.trans('rejected_cookies_description')"></p>
                        <textarea :id="$id('rejected_cookies')" class="large-text ltr" v-model="settings.rejected_cookies" rows="7" cols="15" :placeholder="$wp.trans('rejected_cookies_placeholder')"></textarea>
                    </div>
                </div>
            </TogglePanel>

			<TogglePanel :showSpinner="isLoading">
				<template #title>{{ $wp.trans('advanced_tools') }}</template>

				<div class="row">
					<div class="col-12 my-2">
						<button type="button" class="wpb-button wpb-button-danger" :disabled="isLoading || isSaving || isDeleting" @click="clearWebpCache">
							<Loader v-if="isSaving" color="#fff"></Loader>
							<svg v-else viewBox="0 0 24 24">
								<path d="M14.68 13.25C13.45 14.15 12.54 15.47 12.18 17H6.5L9.25 13.47L11.21 15.83L13.96 12.29L14.68 13.25M5 19V5H19V12.03C19.7 12.09 20.38 12.24 21 12.5V5C21 3.9 20.11 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.11 3.9 21 5 21H12.5C12.24 20.38 12.09 19.7 12.03 19H5M22 18.5V14.5L20.83 15.67C20.11 14.95 19.11 14.5 18 14.5C15.79 14.5 14 16.29 14 18.5S15.79 22.5 18 22.5C19.68 22.5 21.12 21.47 21.71 20H20C19.54 20.61 18.82 21 18 21C16.62 21 15.5 19.88 15.5 18.5S16.62 16 18 16C18.69 16 19.32 16.28 19.77 16.73L18 18.5H22Z" />
							</svg>
							{{ $wp.trans('delete_webp_images') }}
						</button>
						<button type="button" class="wpb-button" :disabled="isLoading || isSaving || isDeleting" @click="scheduleWebpImagesProcess">
							<Loader v-if="isSaving" color="#fff"></Loader>
							<svg v-else viewBox="0 0 24 24">
								<path d="M13.18 19C13.35 19.72 13.64 20.39 14.03 21H5C3.9 21 3 20.11 3 19V5C3 3.9 3.9 3 5 3H19C20.11 3 21 3.9 21 5V11.18C20.5 11.07 20 11 19.5 11C19.33 11 19.17 11 19 11.03V5H5V19H13.18M11.21 15.83L9.25 13.47L6.5 17H13.03C13.14 15.54 13.73 14.22 14.64 13.19L13.96 12.29L11.21 15.83M19 13.5V12L16.75 14.25L19 16.5V15C20.38 15 21.5 16.12 21.5 17.5C21.5 17.9 21.41 18.28 21.24 18.62L22.33 19.71C22.75 19.08 23 18.32 23 17.5C23 15.29 21.21 13.5 19 13.5M19 20C17.62 20 16.5 18.88 16.5 17.5C16.5 17.1 16.59 16.72 16.76 16.38L15.67 15.29C15.25 15.92 15 16.68 15 17.5C15 19.71 16.79 21.5 19 21.5V23L21.25 20.75L19 18.5V20Z" />
							</svg>
							{{ $wp.trans('schedule_webp_images_process') }}
						</button>
					</div>
				</div>
			</TogglePanel>

            <p class="submit">
                <button type="submit" class="wpb-button wpb-button-lg wpb-button-primary" :disabled="isLoading || isSaving || isDeleting">
                    <Loader v-if="isSaving" color="#fff"></Loader>
                    <svg v-else viewBox="0 0 24 24">
                        <path d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z" />
                    </svg>
                    {{ $wp.trans('save_settings') }}
                </button>
            </p>

            <p v-if="!isSaving && !isLoading">
                <button type="button" class="wpb-button wpb-button-sm wpb-button-outlined wpb-button-danger wpb-button-noborder" :disabled="isLoading || isSaving || isDeleting" @click="deleteSettings">
                    <Loader v-if="isDeleting" color="#fff"></Loader>
                    <svg v-else viewBox="0 0 24 24">
                        <path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z" />
                    </svg>
                    {{ $wp.trans('reset_settings') }}
                </button>
            </p>
        </form>
    </div>
</template>

<script>
    import Loader from "../Components/Loader";
    import TogglePanel from "../Components/TogglePanel";
    import API from "../Utilities/Api";

    export default {
        components: {Loader, TogglePanel},
        data() {
            return {
                isLoading: true,
                isSaving: false,
                isDeleting: false,

                settings: {
                    bypass_cache: {
                        single: null,
                        pages: null,
                        frontpage: null,
                        home: null,
                        archives: null,
                        tag: null,
                        category: null,
                        feed: null,
                        search: null,
                        author: null
                    },
                    rejected_uri: null,
                    rejected_user_agent: null,
                }
            };
        },
        async mounted() {
            this.isLoading = true;

            try {
                let response = await API.get('settings');
                this.settings = Object.assign(this.settings, response.data.data);
            } catch(error) {
                console.error(error)
            }

            this.isLoading = false;
        },
        methods: {
			async clearWebpCache() {
				let results = await this.confirm(this.$wp.trans('delete_webp_images'), this.$wp.trans('confirm_delete_webp_images'), 'danger', this.$wp.trans('delete'));
				if(!results.value) {
					return;
				}

				this.isDeleting = true;
				this.isLoading = true;

				try {
					await API.delete('webp');
					this.toast(this.$wp.trans('cache_cleared'), 'success')
				} catch (e) {
					console.error(e);
					this.toast(this.$wp.trans('error_delete_webp_images') + ':' + e.message, 'error');
				}

				this.isLoading = false;
				this.isDeleting = false;
			},
			async scheduleWebpImagesProcess() {
				this.isLoading = true;

				try {
					await API.post('webp');
					this.toast(this.$wp.trans('schedule_webp_images_process_started'), 'success')
				} catch (e) {
					console.error(e);
					this.toast(this.$wp.trans('error_schedule_webp_images_process') + ':' + e.message, 'error');
				}

				this.isLoading = false;
			},
            async deleteSettings() {
                let results = await this.confirm(this.$wp.trans('reset_settings'), this.$wp.trans('confirm_reset_settings'), 'danger', this.$wp.trans('reset'));
                if(!results.value) {
                    return;
                }

                this.isDeleting = true;

                try {
                    let response = await API.delete('settings');
                    this.settings = Object.assign(this.settings, response.data.data)
                    this.toast(this.$wp.trans('settings_saved'), 'success')
                } catch (e) {
                    console.error(e);
                    this.toast(this.$wp.trans('error_saving_settings') + ':' + e.message, 'error');
                }

                this.isDeleting = false;
            },
            async saveSettings() {
                this.isSaving = true;

                try {
                    await API.patch('settings', this.settings);
                    this.toast(this.$wp.trans('settings_saved'), 'success')
                } catch (e) {
                    console.error(e);
                    this.toast(this.$wp.trans('error_saving_settings') + ':' + e.message, 'error');
                }

                this.isSaving = false;
            },
            updateSettings(value, ...fields) {
                for(let field of fields) {
                    this.settings.bypass_cache[field] = value;
                }
            }
        }
    };
</script>

<style>
    .widget + .widget {
        margin-top: 40px;
    }
</style>
